import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Section, EmptyState } from "@/components/Primitives";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function StudentResults() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [annual, setAnnual] = useState(null);

  useEffect(() => {
    api.get(`/analytics/student/${user.id}`).then(r => setAnalytics(r.data));
    api.get(`/analytics/annual/${user.id}`).then(r => setAnnual(r.data));
  }, [user.id]);

  if (!analytics) return <div>Loading…</div>;

  return (
    <div>
      <PageHeader eyebrow="Your progress" title="Results & analysis" subtitle="Tests attempted, monthly trend, strong & weak chapters, and an annual snapshot." />

      <Section title="Monthly trend">
        {analytics.monthly.length === 0 ? <EmptyState title="No data yet" /> : (
          <div className="card-academic p-6" style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.monthly}>
                <CartesianGrid stroke="#D8D3C8" strokeDasharray="3 3" />
                <XAxis dataKey="month" stroke="#526055" tick={{ fontSize: 11 }} />
                <YAxis unit="%" stroke="#526055" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line dataKey="percentage" stroke="#C55743" strokeWidth={2} dot={{ fill: "#1B3C35" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Section>

      <Section title="Chapter performance">
        {analytics.chapters.length === 0 ? <EmptyState title="Attempt tests to see chapter-wise analysis" /> : (
          <div className="card-academic p-6" style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.chapters.slice(0, 12)} layout="vertical">
                <CartesianGrid stroke="#D8D3C8" strokeDasharray="3 3" />
                <XAxis type="number" unit="%" domain={[0, 100]} stroke="#526055" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="chapter" width={140} stroke="#526055" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="percentage" fill="#1B3C35" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Section>

      <Section title="Annual report sheet">
        {!annual || annual.rows.length === 0 ? <EmptyState title="Take more tests to generate annual sheet" /> : (
          <div className="card-academic overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-sand"><tr>
                <th className="p-4 overline text-left">Subject</th>
                <th className="p-4 overline text-left">Tests</th>
                <th className="p-4 overline text-left">Score</th>
                <th className="p-4 overline text-right">Percentage</th>
              </tr></thead>
              <tbody>
                {annual.rows.map(r => (
                  <tr key={r.subject} className="border-b border-stone">
                    <td className="p-4">{r.subject}</td>
                    <td className="p-4 font-mono">{r.tests_taken}</td>
                    <td className="p-4 font-mono">{r.score} / {r.max_score}</td>
                    <td className="p-4 text-right font-mono font-semibold text-forest">{r.percentage}%</td>
                  </tr>
                ))}
                <tr className="bg-forest text-ivory">
                  <td className="p-4 font-serif text-lg">Overall</td>
                  <td className="p-4 font-mono">{annual.rows.reduce((s, r) => s + r.tests_taken, 0)}</td>
                  <td className="p-4 font-mono">{annual.total_score} / {annual.total_max}</td>
                  <td className="p-4 text-right font-mono text-xl font-semibold">{annual.overall_percentage}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Recent attempts">
        {analytics.recent_attempts.length === 0 ? <EmptyState /> : (
          <div className="card-academic overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-sand"><tr>
                <th className="p-4 overline text-left">Test</th>
                <th className="p-4 overline text-left">Date</th>
                <th className="p-4 overline text-left">Score</th>
                <th className="p-4 overline text-right">%</th>
              </tr></thead>
              <tbody>
                {analytics.recent_attempts.map(a => (
                  <tr key={a.id} className="border-b border-stone">
                    <td className="p-4">{a.test_title}</td>
                    <td className="p-4 font-mono text-xs">{a.submitted_at?.slice(0, 10)}</td>
                    <td className="p-4 font-mono">{a.score} / {a.max_score}</td>
                    <td className="p-4 text-right font-mono text-forest">{a.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
